import clsx from 'clsx'
import { useEffect, useRef } from 'react'
import '../styles/slp.css'

/**
 * Professional dBFS meter (rt UI v2 spec §专业电平表).
 *
 *  - Horizontal variant: SLP "Audio Levels" style segmented bar.
 *  - Vertical variant:   big studio meter with full scale + REF -18 line.
 *  - Scale ticks 0/-3/-6/-12/-18/-24/-36/-48/-60 (lines + numbers, DOM text).
 *  - Zones: green -60..-18, yellow -18..-6, red -6..0; LED segments ~1.5 dB.
 *  - Ballistics: attack 1 ms / release 300 ms, rAF-smoothed internally —
 *    callers just feed `peakDbfs` (instantaneous peak, any update rate).
 *  - Peak hold: 2 s hold then 20 dB/s decay; numeric readout (e.g. -8.4)
 *    carried on a `data-peak` attribute.
 *  - CLIP latch: input ≥ -0.1 dBFS lights a latched red lamp; every rising
 *    edge also fires `onClip` (sidecar clip counting).
 *  - Clicking ANYWHERE on the meter body resets peak hold + CLIP (Jeff).
 *
 * Rendering: LED bar is a canvas driven imperatively from the rAF loop (no
 * React re-render per frame); scale numbers / peak readout / CLIP lamp are
 * DOM nodes so tests can assert on text ('-18') and attributes.
 */

const MIN_DB = -60
const MAX_DB = 0
const SEG_DB = 1.5
const SEG_COUNT = Math.round((MAX_DB - MIN_DB) / SEG_DB) // 40 LEDs
const TICKS = [0, -3, -6, -12, -18, -24, -36, -48, -60]
const ATTACK_TAU_S = 0.001
const RELEASE_TAU_S = 0.3
const PEAK_HOLD_MS = 2000
const PEAK_DECAY_DB_S = 20
const CLIP_AT_DB = -0.1
const REF_DB = -18
const READOUT_INTERVAL_MS = 100

/** 0..1 position of a dB value on the linear-in-dB scale. */
function frac(db: number): number {
  return Math.max(0, Math.min(1, (db - MIN_DB) / (MAX_DB - MIN_DB)))
}

function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

interface Palette {
  track: string
  green: string
  yellow: string
  red: string
  peak: string
  ref: string
}

function readPalette(): Palette {
  return {
    track: cssVar('--slp-meter-track', '#1c2429'),
    green: cssVar('--slp-meter-green', '#2ee56b'),
    yellow: cssVar('--slp-meter-yellow', '#ffd23f'),
    red: cssVar('--slp-meter-red', '#ff4136'),
    peak: cssVar('--slp-meter-peak', '#ffffff'),
    ref: cssVar('--slp-meter-ref', '#ffffff'),
  }
}

function zoneColor(p: Palette, segHighDb: number): string {
  if (segHighDb <= -18) return p.green
  if (segHighDb <= -6) return p.yellow
  return p.red
}

function draw(
  canvas: HTMLCanvasElement,
  p: Palette,
  vertical: boolean,
  displayDb: number,
  peakDb: number,
): void {
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const w = canvas.width
  const h = canvas.height
  if (w === 0 || h === 0) return
  const dpr = window.devicePixelRatio || 1
  ctx.clearRect(0, 0, w, h)
  ctx.fillStyle = p.track
  ctx.fillRect(0, 0, w, h)

  const len = vertical ? h : w // axis length in device px
  const gap = Math.max(1, Math.round(dpr)) // px gap between LEDs
  const segLen = len / SEG_COUNT
  const inset = Math.max(2, Math.round(2 * dpr)) // cross-axis inset

  for (let i = 0; i < SEG_COUNT; i++) {
    const segLow = MIN_DB + i * SEG_DB
    const segHigh = segLow + SEG_DB
    ctx.fillStyle = zoneColor(p, segHigh)
    ctx.globalAlpha = displayDb >= segLow ? 1 : 0.16
    if (vertical) {
      const y = h - (i + 1) * segLen + gap / 2
      ctx.fillRect(inset, y, w - inset * 2, Math.max(1, segLen - gap))
    } else {
      const x = i * segLen + gap / 2
      ctx.fillRect(x, inset, Math.max(1, segLen - gap), h - inset * 2)
    }
  }
  ctx.globalAlpha = 1

  // REF -18 white reference line (vertical big-meter variant)
  if (vertical) {
    const y = h - frac(REF_DB) * h
    ctx.fillStyle = p.ref
    ctx.globalAlpha = 0.9
    ctx.fillRect(0, Math.round(y) - 1, w, Math.max(1, Math.round(dpr)))
    ctx.globalAlpha = 1
  }

  // Peak-hold line (yellow on the SLP horizontal bar, white on the big meter)
  if (peakDb > MIN_DB) {
    const lineW = Math.max(2, Math.round(2 * dpr))
    ctx.fillStyle = vertical ? p.peak : p.yellow
    if (vertical) {
      const y = h - frac(peakDb) * h
      ctx.fillRect(0, Math.min(h - lineW, Math.max(0, Math.round(y) - lineW / 2)), w, lineW)
    } else {
      const x = frac(peakDb) * w
      ctx.fillRect(Math.min(w - lineW, Math.max(0, Math.round(x) - lineW / 2)), 0, lineW, h)
    }
  }
}

export interface LevelMeterProProps {
  /** Stable id — root carries data-testid="rt-meter-<id>". */
  id: string
  /** Instantaneous peak level in dBFS (e.g. from useAudioLevel). */
  peakDbfs: number
  variant?: 'horizontal' | 'vertical'
  /** Optional row label shown before the bar (horizontal) / above (vertical). */
  label?: string
  /** Bar length in px for the vertical variant (default 240). */
  height?: number
  /** Fired on every clip event (rising edge through -0.1 dBFS). */
  onClip?: () => void
  className?: string
}

export function LevelMeterPro({
  id,
  peakDbfs,
  variant = 'horizontal',
  label,
  height = 240,
  onClip,
  className,
}: LevelMeterProProps) {
  const vertical = variant === 'vertical'
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const readoutRef = useRef<HTMLSpanElement | null>(null)
  const clipRef = useRef<HTMLSpanElement | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)

  // Latest prop values readable from the rAF loop without re-subscribing.
  const inputRef = useRef(peakDbfs)
  inputRef.current = peakDbfs
  const onClipRef = useRef(onClip)
  onClipRef.current = onClip

  // Click-to-reset implementation is installed by the effect below.
  const resetRef = useRef<(() => void) | null>(null)

  // Meter state lives in refs — the loop never triggers React renders.
  const stateRef = useRef({
    displayDb: -100,
    peakDb: -100,
    peakAt: 0,
    clipLatched: false,
    wasAbove: false,
    lastT: 0,
    lastReadoutT: 0,
  })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const palette = readPalette()

    // Keep the canvas backing store in sync with CSS size (dpr-aware).
    // Narrow horizontal bars also flip a compact flag that hides the -3/-24
    // labels (tick lines stay) so the hot-end numbers never collide.
    const fit = () => {
      const dpr = window.devicePixelRatio || 1
      const r = canvas.getBoundingClientRect()
      const bw = Math.max(1, Math.round(r.width * dpr))
      const bh = Math.max(1, Math.round(r.height * dpr))
      if (canvas.width !== bw) canvas.width = bw
      if (canvas.height !== bh) canvas.height = bh
      if (!vertical) rootRef.current?.setAttribute('data-compact', r.width < 250 ? '1' : '0')
    }
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(canvas)

    const syncReadout = () => {
      const s = stateRef.current
      const el = readoutRef.current
      if (el) {
        const txt = s.peakDb <= -99 ? '-∞' : s.peakDb.toFixed(1)
        el.textContent = txt
        el.setAttribute('data-peak', s.peakDb.toFixed(1))
      }
      const lamp = clipRef.current
      if (lamp) lamp.setAttribute('data-clip', s.clipLatched ? '1' : '0')
      rootRef.current?.setAttribute(
        'aria-valuenow',
        String(Math.round(Math.max(MIN_DB, s.displayDb))),
      )
    }
    // Expose the reset for the click handler (defined outside this effect).
    resetRef.current = () => {
      const s = stateRef.current
      s.peakDb = s.displayDb
      s.peakAt = performance.now()
      s.clipLatched = false
      syncReadout()
      draw(canvas, palette, vertical, s.displayDb, s.peakDb)
    }

    let raf = 0
    const tick = (now: number) => {
      const s = stateRef.current
      const dt = s.lastT ? Math.min(0.1, (now - s.lastT) / 1000) : 1 / 60
      s.lastT = now

      const raw = inputRef.current
      const target = Number.isFinite(raw) ? Math.min(MAX_DB, Math.max(-100, raw)) : -100

      // CLIP latch — rising edge through -0.1 dBFS fires onClip every time.
      if (Number.isFinite(raw) && raw >= CLIP_AT_DB) {
        if (!s.wasAbove) {
          s.wasAbove = true
          s.clipLatched = true
          onClipRef.current?.()
        }
      } else {
        s.wasAbove = false
      }

      // Ballistics: attack 1 ms, release 300 ms (exponential time constants).
      const tau = target > s.displayDb ? ATTACK_TAU_S : RELEASE_TAU_S
      s.displayDb += (target - s.displayDb) * (1 - Math.exp(-dt / tau))

      // Peak hold: track raw peaks, hold 2 s, then decay 20 dB/s.
      if (target >= s.peakDb) {
        s.peakDb = target
        s.peakAt = now
      } else if (now - s.peakAt > PEAK_HOLD_MS) {
        s.peakDb = Math.max(target, s.peakDb - PEAK_DECAY_DB_S * dt)
      }

      draw(canvas, palette, vertical, s.displayDb, s.peakDb)
      if (now - s.lastReadoutT > READOUT_INTERVAL_MS) {
        s.lastReadoutT = now
        syncReadout()
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      resetRef.current = null
    }
  }, [vertical])

  const scale = (
    <div
      aria-hidden="true"
      className={clsx(
        'relative select-none font-mono leading-none text-[var(--slp-text-dim)]',
        vertical ? 'h-full w-9 text-[10px]' : 'mt-0.5 h-3 w-full text-[9px]',
      )}
    >
      {TICKS.map((t) => {
        const pct = frac(t) * 100
        return vertical ? (
          <span
            key={t}
            className="absolute left-0 flex items-center gap-1"
            style={{ bottom: `${pct}%`, transform: 'translateY(50%)' }}
          >
            <span className="inline-block h-px w-1.5 bg-[var(--slp-border-strong)]" />
            <span>{t}</span>
            {t === REF_DB ? (
              <span className="rounded-sm bg-[var(--slp-text)] px-0.5 py-px text-[8px] font-bold text-white">
                REF
              </span>
            ) : null}
          </span>
        ) : (
          <span
            key={t}
            className="absolute top-0 flex -translate-x-1/2 flex-col items-center"
            style={{ left: `${pct}%` }}
          >
            <span className="mb-px inline-block h-1 w-px bg-[var(--slp-border-strong)]" />
            <span className={t === -3 || t === -24 ? 'slp-tick-thin' : undefined}>{t}</span>
          </span>
        )
      })}
    </div>
  )

  const readout = (
    <span
      ref={readoutRef}
      data-testid={`rt-meter-${id}-peak`}
      data-peak="-100.0"
      className={clsx(
        'select-none font-mono tabular-nums text-[var(--slp-text)]',
        vertical ? 'text-[15px] font-semibold' : 'w-12 text-right text-[12px]',
      )}
    >
      -∞
    </span>
  )

  const clipLamp = (
    <span
      ref={clipRef}
      data-testid={`rt-meter-${id}-clip`}
      data-clip="0"
      className={clsx(
        'select-none rounded-sm px-1 py-px text-center text-[9px] font-bold leading-tight',
        'bg-[var(--slp-panel-2)] text-[var(--slp-text-dim)]',
        'data-[clip="1"]:bg-[var(--slp-meter-red)] data-[clip="1"]:text-white',
      )}
    >
      CLIP
    </span>
  )

  return (
    <div
      ref={rootRef}
      data-testid={`rt-meter-${id}`}
      data-variant={variant}
      role="meter"
      aria-label={label ? `${label} level` : 'Audio level'}
      aria-valuemin={MIN_DB}
      aria-valuemax={MAX_DB}
      aria-valuenow={MIN_DB}
      onClick={() => resetRef.current?.()}
      title="Click to reset peak hold + CLIP"
      className={clsx('cursor-pointer', vertical ? 'inline-flex flex-col items-center gap-1.5' : 'w-full', className)}
    >
      {vertical ? (
        <>
          {label ? <span className="text-[12px] font-semibold text-[var(--slp-text)]">{label}</span> : null}
          <div className="flex items-center gap-2">
            {readout}
            {clipLamp}
          </div>
          <div className="flex items-stretch gap-1.5" style={{ height }}>
            <canvas ref={canvasRef} className="block h-full w-7 rounded-sm" />
            {scale}
          </div>
        </>
      ) : (
        <div className="flex items-start gap-2">
          {label ? (
            <span className="w-14 shrink-0 pt-px text-[12px] text-[var(--slp-text)]">{label}</span>
          ) : null}
          <div className="min-w-0 flex-1">
            <canvas ref={canvasRef} className="block h-3.5 w-full rounded-sm" />
            {scale}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-0.5">
            {readout}
            {clipLamp}
          </div>
        </div>
      )}
    </div>
  )
}
